/**
 * QuickAddDrawer — ajout intelligent d'un domaine « à découvrir » : un seul champ qui accepte
 * un lien Google Maps, une URL de site, un texte libre (ou une photo : carte de visite/panneau).
 * Détection automatique de la source, extraction côté serveur, champs éditables, alerte doublon,
 * notes horodatées. Enregistrement en un clic.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Camera, Link2, AlertTriangle } from 'lucide-react';
import { vignoblesApi } from '../../services/api';
import { Drawer } from '../../components/ui/Drawer';
import { Button } from '../../components/ui/kit';
import CameraView from '../scanner/CameraView';

const FIELDS = [
  ['name', 'Nom du domaine'], ['website', 'Site web'], ['address', 'Adresse'],
  ['postal_code', 'Code postal'], ['city', 'Ville'], ['phone', 'Téléphone'], ['email', 'E-mail'],
];

export default function QuickAddDrawer({ open, onClose, onSaved, initialInput = '' }) {
  const [input, setInput] = useState(initialInput);
  const [mode, setMode] = useState('link');   // link | photo
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [duplicate, setDuplicate] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setInput(''); setFields(null); setCandidates([]); setDuplicate(null); setNote(''); setMode('link'); };

  const analyze = async (value) => {
    if (!value?.trim()) return;
    setLoading(true);
    try {
      const res = await vignoblesApi.extractReco(value.trim());
      setFields({ source_type: 'manuel', ...res.fields });
      setCandidates(res.candidates || []);
      setDuplicate(res.duplicate);
      if (res.fields?.error) toast.warning(res.fields.error);
    } catch (e) { toast.error(e.message || 'Analyse impossible'); }
    finally { setLoading(false); }
  };

  const pickCandidate = (c) => {
    setFields((f) => {
      const next = { ...f };
      ['name', 'website', 'address', 'postal_code', 'city', 'phone', 'region'].forEach((k) => { if (c[k] && !next[k]) next[k] = c[k]; });
      return next;
    });
    setCandidates([]);
    toast.success(`« ${c.name} » repris`);
  };

  const analyzePhoto = async (b64) => {
    setLoading(true);
    try {
      const res = await vignoblesApi.extractRecoPhoto(b64);
      setFields({ source_type: 'photo', ...res.fields });
      setDuplicate(res.duplicate);
      setMode('link');
    } catch (e) { toast.error(e.message || 'Lecture impossible'); }
    finally { setLoading(false); }
  };

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!fields?.name?.trim()) { toast.error('Le nom est requis'); return; }
    setSaving(true);
    try {
      await vignoblesApi.createReco({ ...fields, note: note || undefined });
      toast.success('Domaine ajouté « à découvrir »');
      reset();
      onSaved();
    } catch (e) { toast.error(e.message || "Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <Drawer open={open} onClose={onClose} title="Ajouter à découvrir" testId="quickadd-drawer"
      footer={fields && (
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Fermer</Button>
          <Button variant="primary" disabled={saving || !fields.name?.trim()} onClick={save} data-testid="quickadd-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Enregistrer
          </Button>
        </div>
      )}>
      {/* Bascule lien/texte ↔ photo */}
      <div className="flex gap-2 mb-3">
        <button onClick={() => setMode('link')} className={`vd-btn h-8 text-[12px] ${mode === 'link' ? 'vd-btn-primary' : 'vd-btn-ghost'}`} data-testid="quickadd-mode-link"><Link2 className="w-3.5 h-3.5" /> Lien ou texte</button>
        <button onClick={() => setMode('photo')} className={`vd-btn h-8 text-[12px] ${mode === 'photo' ? 'vd-btn-primary' : 'vd-btn-ghost'}`} data-testid="quickadd-mode-photo"><Camera className="w-3.5 h-3.5" /> Photo</button>
      </div>

      {mode === 'link' ? (
        <div className="flex gap-2">
          <input
            className="vd-input h-10 flex-1" placeholder="Collez un lien Google Maps, une URL de site, ou un nom…"
            value={input} onChange={(e) => setInput(e.target.value)}
            onPaste={(e) => { const v = e.clipboardData.getData('text'); setTimeout(() => analyze(v), 50); }}
            onKeyDown={(e) => { if (e.key === 'Enter') analyze(input); }}
            data-testid="quickadd-input"
          />
          <Button variant="primary" onClick={() => analyze(input)} disabled={loading} data-testid="quickadd-analyze">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Analyser'}
          </Button>
        </div>
      ) : (
        <CameraView onResult={(b64) => analyzePhoto(b64)} compact captureLabel="Photographier la carte / le panneau" />
      )}

      {loading && !fields && <p className="text-[13px] text-n600 mt-3 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Recherche des informations…</p>}

      {duplicate && (
        <div className="mt-3 vd-card p-2.5 bg-accent-100 border-accent-300 flex items-center gap-2 text-[13px]" data-testid="quickadd-duplicate">
          <AlertTriangle className="w-4 h-4 text-accent-700 shrink-0" />
          Déjà enregistré comme {duplicate.kind === 'domaine' ? 'domaine' : 'recommandation'} : <strong>{duplicate.name}</strong>
        </div>
      )}

      {candidates.length > 0 && (
        <div className="mt-4" data-testid="quickadd-candidates">
          <p className="kicker mb-2">Domaines trouvés — sélectionnez pour pré-remplir</p>
          <div className="space-y-2">
            {candidates.map((c, i) => (
              <button key={i} onClick={() => pickCandidate(c)} className="w-full text-left vd-card p-2.5 hover:bg-surface" data-testid={`quickadd-candidate-${i}`}>
                <p className="font-title text-lg">{c.name}</p>
                <p className="text-[12px] text-n600">{[c.city, c.region, c.website].filter(Boolean).join(' · ')}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {fields && (
        <div className="mt-4 space-y-3" data-testid="quickadd-fields">
          {FIELDS.map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-[12px] text-n600">{label}</span>
              <input className="vd-input h-9" value={fields[k] || ''} onChange={(e) => setField(k, e.target.value)} data-testid={`quickadd-field-${k}`} />
            </label>
          ))}
          <label className="block">
            <span className="text-[12px] text-n600">Notes</span>
            <textarea className="vd-input py-2 h-auto" rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="quickadd-note" placeholder="Pourquoi ce domaine vous intéresse…" />
          </label>
        </div>
      )}
    </Drawer>
  );
}
